import { forwardRef, useId } from 'react';

/**
 * A fieldset of mutually-exclusive radio choices (spec §7.1). Works either
 * controlled (`value` + `onChange`) or uncontrolled (`defaultValue`).
 *
 * @param {object} props
 * @param {string} [props.name] - shared `name` for the radio inputs; generated
 *   when omitted.
 * @param {import('react').ReactNode} [props.legend]
 * @param {{value: string, label: import('react').ReactNode, hint?: import('react').ReactNode, disabled?: boolean}[]} props.options
 * @param {string} [props.value] - controlled selected value.
 * @param {string} [props.defaultValue] - uncontrolled initial value.
 * @param {(value: string) => void} [props.onChange]
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error]
 * @param {string} [props.className]
 */
export const RadioGroup = forwardRef(function RadioGroup(
  { name, legend, options, value, defaultValue, onChange, hint, error, className = '', ...rest },
  ref
) {
  const generatedName = useId();
  const groupName = name ?? generatedName;
  const hintId = hint ? `${groupName}-hint` : undefined;
  const errorId = error ? `${groupName}-error` : undefined;
  const isControlled = value !== undefined;

  return (
    <fieldset
      ref={ref}
      className={`etch-radio-group ${className}`.trim()}
      aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
      aria-invalid={error ? 'true' : undefined}
      {...rest}
    >
      {legend && <legend>{legend}</legend>}
      {options.map((option) => {
        const optionId = `${groupName}-${option.value}`;
        return (
          <label key={option.value} htmlFor={optionId} className="etch-choice">
            <input
              type="radio"
              id={optionId}
              name={groupName}
              value={option.value}
              checked={isControlled ? value === option.value : undefined}
              defaultChecked={!isControlled ? defaultValue === option.value : undefined}
              disabled={option.disabled}
              onChange={() => onChange?.(option.value)}
            />
            <span className="etch-choice__body">
              <span className="etch-choice__label">{option.label}</span>
              {option.hint && <span className="etch-choice__hint">{option.hint}</span>}
            </span>
          </label>
        );
      })}
      {error ? (
        <span id={errorId} className="etch-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="etch-field__hint">
          {hint}
        </span>
      ) : null}
    </fieldset>
  );
});

export default RadioGroup;
